function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      
          modal.show(button);
       
      
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    console.log('Please check again its working or not')
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }

     // new code
     const quickViewParent = target.closest('[data-original-section="product-quick-view"]');

      if (!quickViewParent) {

      var customDropdown = document.querySelector(".custom-dropdown_sticky");
      var customSelected = customDropdown?.querySelector(".custom-dropdown__selected");
      var customList = customDropdown?.querySelector(".custom-dropdown__list");

      const value_get = target.value;
      const isDisabled = target.classList.contains("disabled");
      
      if (customDropdown && customSelected) {
                // customSelected.childNodes[0].textContent = value; // set text
                customSelected.childNodes[0].textContent = value.length > 4 ? value.slice(0, 4) : value;
                // mark active in dropdown list
                customList.querySelectorAll("li").forEach(li => {
                  li.classList.remove("active");
                  if (li.getAttribute("data-value") === value) {
                    li.classList.add("active");
                  }
                });
      }
      const customSelected_sticky = document.querySelector(".custom-dropdown__selected");
      if (customSelected_sticky && customSelected_sticky.classList.contains("error")) {
        customSelected_sticky.classList.remove("error"); // remove error once selected
      }

      if(document.querySelectorAll('.sticky_addtocart_custom').length > 0){
        const stickyText = document.querySelector('.sticky_addtocart_custom [data-add-to-cart-text]');
        if (stickyText) {
          stickyText.textContent = isDisabled ? "Sold out" : "Add to Cart";
          if (isDisabled) {
            stickyText.setAttribute("disabled", "disabled");
            document.querySelector('.sticky__btns').classList.add('sold-btn-j');
          } else {
            stickyText.removeAttribute("disabled");
              document.querySelector('.sticky__btns').classList.remove('sold-btn-j');
          }

        }
      }
      const stickyBuyBtn = document.querySelector(".sticky__btns .shopify-payment-button_sticky .shopify-payment-button__button");
      console.log(isDisabled, 'stickyBtn')
        if (stickyBuyBtn) {

          if (isDisabled) {
            stickyBuyBtn.textContent = "NOTIFY ME";
            stickyBuyBtn.classList.add("notify_mode");
          } else {
            stickyBuyBtn.textContent = "Buy Now";
            stickyBuyBtn.classList.remove("notify_mode");
          }
        }
    } 
    // end new code


  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


/*============= Start [THTR-DEV-JUL-391 Perfume Quiz] =====================*/

document.addEventListener("DOMContentLoaded", function () {
  const startBtn = document.querySelector(".take__start_quiz");
  const introSection = document.querySelector(".take_the_quiz");

  if (startBtn) {
    startBtn.addEventListener("click", function (e) {
      e.preventDefault();
      const q1 = document.getElementById("question_1");
      if (q1) {
        q1.classList.remove("hide");
        if (introSection) introSection.style.display = "none";
          const homepageBanner = document.querySelector(".perfume__homepage_banner");
        if (homepageBanner && homepageBanner.classList.contains("first_image")) {
          homepageBanner.classList.remove("first_image");
        }
        
        //q1.scrollIntoView({ behavior: "smooth" });
      }
    });
  }

  const questionSelectors = document.querySelectorAll('.common__question');
  questionSelectors.forEach((section, index) => {
    const inputs = section.querySelectorAll('input[type="radio"]');
    inputs.forEach(input => {
      input.addEventListener("change", function () {
        section.classList.add("hide");
        const nextSection = questionSelectors[index + 1];
        if (nextSection) {
          nextSection.classList.remove("hide");
          //nextSection.scrollIntoView({ behavior: "smooth" });

        } else {
          calculateScore();
        }
      });
    });
  });

  function getSelectedValue(name) {
    const input = document.querySelector(`input[name="${name}"]:checked`);
    return input ? input.value : null;
  }

  const productHandles = {
    "Tragedy": "/products/tragedy-30ml",
    "Cask": "/products/cask-30ml",
    "Djinn": "/products/djinn",
    "Grotesque": "/products/grotesque-30ml",
    "Bucky": "/products/theater-bucky",
    "Mariyam": "/products/mariyam",
    "Buttons": "/products/theater-buttons",
    "Musika": "/products/musika-50ml",
    "Biscuit": "/products/theater-biscuit",
    "Babylon": "/products/babylon",
    "Ballad": "/products/ballad-30ml",
    "Ghoul": "/products/ghoul-50ml",
    "Mead": "/products/mead-30ml",
    "Distillery": "/products/distillery-30ml",
    "Kabus": "/products/kabus-50ml"
  };

  function calculateScore() {
    const answers = {
      q1: getSelectedValue("quiz_first"),   
      q2: getSelectedValue("quiz_two"), 
      q3: getSelectedValue("quiz_three"),   
      q4: getSelectedValue("quiz_four"),  
      q5: getSelectedValue("quiz_five")  
    };

    const quizData = {
      zodiac: {
        "Aries / Leo / Sagittarius": ["Djinn", "Grotesque", "Bucky", "Distillery"],
        "Taurus / Virgo / Capricorn": ["Cask", "Babylon", "Biscuit", "Ballad"],
        "Gemini / Libra / Aquarius": ["Mariyam", "Musika", "Buttons", "Kabus"],
        "Cancer / Scorpio / Pisces": ["Tragedy", "Ghoul", "Buttons", "Musika"],
        "I don’t care": []
      },
      quote: {
        "I don’t do ‘subtle.’": ["Grotesque", "Biscuit", "Kabus"],
        "Not all those who wander are lost.": ["Mariyam", "Buttons", "Musika"],
        "Darling, I am the drama.": ["Tragedy", "Bucky", "Babylon"],
        "Let the wild winds carry me.": ["Djinn", "Bucky", "Distillery"],
        "I don’t chase, I attract.": ["Babylon", "Buttons", "Biscuit", "Mead"]
      },
      aesthetic: {
        "Moody + Minimal": ["Cask", "Ghoul", "Djinn", "Buttons", "Bucky"],
        "Soft + Poetic": ["Ballad", "Tragedy", "Mead", "Buttons"],
        "Raw + Rugged": ["Cask", "Djinn", "Distillery", "Biscuit"],
        "Vibrant + Surreal": ["Tragedy", "Grotesque", "Mariyam", "Bucky"]
      },
      personality: {
        "The Mysterious One": ["Djinn", "Ghoul", "Cask"],
        "The Free Spirit": ["Mariyam"],
        "The Romantic": ["Ballad", "Grotesque", "Tragedy", "Mariyam"],
        "The Rule Breaker": ["Djinn", "Ghoul", "Grotesque"],
        "The Old Soul": ["Tragedy", "Cask"]
      }, 
      memory: {
        "The Scent of Rain on Dry Earth": ["Djinn", "Cask"],
        "A Sun-Warmed T-Shirt of Someone You Love": ["Mariyam", "Tragedy"],
        "Freshly Peeled Orange in a Summer Kitchen": ["Tragedy", "Mariyam", "Grotesque"],
        "Books and Dust in an Old Library": ["Ghoul", "Cask", "Biscuit", "Buttons"],
        "Smoke from a Winter Bonfire": ["Ghoul", "Djinn", "Grotesque", "Biscuit", "Bucky"]
      }
    };

    const scoreMap = {};
 
    function addScore(products, weight) {
      if (!products) return;
      products.forEach((p) => {
        scoreMap[p] = (scoreMap[p] || 0) + weight;
      });
    }
   
    const q1Products = quizData.zodiac[answers.q1];
    addScore(q1Products, 0.5);
    //console.log("Q1:", answers.q1, "| Products:", q1Products, "| Weight: 0.5");

    const q2Products = quizData.quote[answers.q2];
    addScore(q2Products, 0.5);
   // console.log("Q2:", answers.q2, "| Products:", q2Products, "| Weight: 0.5");

    const q3Products = quizData.aesthetic[answers.q3];
    addScore(q3Products, 1);
   // console.log("Q3:", answers.q3, "| Products:", q3Products, "| Weight: 1");

    const q4Products = quizData.personality[answers.q4];
    addScore(q4Products, 1);
    //console.log("Q4:", answers.q4, "| Products:", q4Products, "| Weight: 1");

    const q5Products = quizData.memory[answers.q5];
    addScore(q5Products, 1);
    //console.log("Q5:", answers.q5, "| Products:", q5Products, "| Weight: 1");

   // console.log("Final Score Map:", scoreMap);

 
    let maxScore = 0;
    let topProducts = [];

    for (const [product, score] of Object.entries(scoreMap)) {
      if (score > maxScore) {
        maxScore = score;
        topProducts = [product];
      } else if (score === maxScore) {
        topProducts.push(product);
      }
    }

    if (topProducts.length > 1) {
      const allEqual = topProducts.every(p => scoreMap[p] === maxScore);
      
      if (allEqual) {
        topProducts = [topProducts[0]];
      }
    }

    showResult(topProducts);
  }

  function showResult(products) {
    const resultContainer = document.querySelector(".result_of_quiz");
    const resultInner = resultContainer.querySelector(".result_of_quiz_inner");
    resultContainer.classList.remove("hide");
    resultInner.innerHTML = ""; 

    const resultHTML = products.map(productName => {
      const handle = productHandles[productName];

      if (!handle) return;

      return fetch(`${handle}.js`)
        .then(res => res.json())
        .then(product => {
          const image = product.images?.[0] || '';
          const title = product.title;
          const url = product.url || handle;

          const html = `
            <div class="single_result_product">
              <h3>${title}</h3>
              <div class="quiz_pro_image">${image ? `<img src="${image}" alt="${title}">` : ''}</div>
              <a href="${url}" class="bttn bttn--secondary">See Perfume Details</a>
            </div>
          `;
          resultInner.insertAdjacentHTML("beforeend", html);
        });
    });

    // Promise.all(resultHTML).then(() => {
    //   resultContainer.scrollIntoView({ behavior: "smooth" });
    // });
  }
});

/*========== END ==============*/

/* -------------------------- [ THTR-DEV-AUG-403 Sticky Add To Cart ] ---------------------------------*/

if(document.querySelectorAll('.custom-dropdown_sticky').length > 0){
document.querySelectorAll('.custom-dropdown_sticky').forEach(dropdown => {
  const selected = dropdown.querySelector('.custom-dropdown__selected');
  const list = dropdown.querySelector('.custom-dropdown__list');
  const liItems = list.querySelectorAll('li');

  // Dropdown open/close
  selected.addEventListener('click', () => {
    const isOpen = dropdown.classList.contains('open');

    document.querySelectorAll('.custom-dropdown_sticky').forEach(d => {
      d.classList.remove('open');
      d.querySelector('.custom-dropdown__list').style.display = 'none';
      document.body.classList.remove('no-scroll-j');
    });

    if (!isOpen) {
      dropdown.classList.add('open');
      list.style.display = 'block';
      document.body.classList.add('no-scroll-j');
    }
  });

  // Handle selection
  liItems.forEach(item => {
    item.addEventListener('click', () => {
      const value = item.getAttribute('data-value');
      const isDisabled = item.classList.contains('disabled');

      // update dropdown label
      selected.childNodes[0].textContent = value;

      const customSelected = document.querySelector(".custom-dropdown__selected");
      if (customSelected && customSelected.classList.contains("error")) {
        customSelected.classList.remove("error"); // remove error once selected
      }

      // sync with chip button (if .size__chip_option exists)
      /*const chipWrapper = document.querySelector('.size__variant_picker');
      if (chipWrapper) {
        const chipButton = chipWrapper.querySelector(
          `.chip-wrapper input[value="${value}"]`
        );
        if (chipButton) {
          chipButton.click(); // simulate real selection
        }
      }*/

      function lockScroll() {
        const x = window.scrollX;
        const y = window.scrollY;

        // Freeze the page at current position
        document.body.style.position = 'fixed';
        document.body.style.top = `-${y}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';

        return () => {
          // Restore scroll
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.left = '';
          document.body.style.right = '';
          document.body.style.width = '';
          window.scrollTo(x, y);
        };
      }

      const chipWrapper = document.querySelector('.size__variant_picker');
      if (chipWrapper) {
        const chipButton = chipWrapper.querySelector(
          `.chip-wrapper input[value="${value}"]`
        );
        if (chipButton) {
          // Lock before clicking
          const unlock = lockScroll();

          chipButton.click(); // Dawn's JS runs

          // Wait a bit to let Dawn finish its async stuff, then unlock
          setTimeout(() => {
            unlock();
          }, 5000); // you can bump this up if Dawn takes longer
        }
      }

      // update sticky add to cart text
      if(document.querySelectorAll('.sticky_addtocart_custom').length > 0){
            const stickyText = document.querySelector('.sticky_addtocart_custom [data-add-to-cart-text]');
            if (stickyText) {
              stickyText.textContent = isDisabled ? "Sold out" : "Add to Cart";

               if (isDisabled) {
                stickyText.setAttribute("disabled", "disabled");
              } else {
                stickyText.removeAttribute("disabled");
              }

            }
      }
      const stickyBuyBtn = document.querySelector(".sticky__btns .shopify-payment-button_sticky .shopify-payment-button__button");
      if (stickyBuyBtn) {
        if (isDisabled) {
          stickyBuyBtn.textContent = "NOTIFY ME";
          stickyBuyBtn.classList.add("notify_mode");
        } else {
          stickyBuyBtn.textContent = "Buy Now";
          stickyBuyBtn.classList.remove("notify_mode");
        }
      }

      // close dropdown
      dropdown.classList.remove('open');
      list.style.display = 'none';
      document.body.classList.remove('no-scroll-j');
    });
  });

  // Close outside
  document.addEventListener('click', (e) => {
    if (!dropdown.contains(e.target)) {
      dropdown.classList.remove('open');
      list.style.display = 'none';
       document.body.classList.remove('no-scroll-j');
    }
  });
});
}

if(document.querySelectorAll(".sticky_addtocart_custom button").length > 0){
document.querySelector(".sticky_addtocart_custom button").addEventListener("click", function () {
  const customSelected = document.querySelector(".custom-dropdown__selected");
  const selectedText = customSelected ? customSelected.childNodes[0].textContent.trim() : "";

  if(this.classList.contains('default_addtocart')){

    const realATC = document.querySelector(".product-form__submit");
  
    if (realATC) {
      realATC.click();

      const stickyText = document.querySelector(".sticky_addtocart_custom [data-add-to-cart-text]");
      if (stickyText) {
        const originalStickyText = stickyText.textContent;
        stickyText.textContent = "Added";
        setTimeout(() => {
          stickyText.textContent = originalStickyText;
        }, 2000);
      }

    }

  }else{
  if (!selectedText || selectedText === "Size") {
    if (customSelected) {
      customSelected.classList.add("error"); 
    }
  } else {
    
    const realATC = document.querySelector(".product-form__submit");
  
    if (realATC) {
      realATC.click();

      const stickyText = document.querySelector(".sticky_addtocart_custom [data-add-to-cart-text]");
      if (stickyText) {
        const originalStickyText = stickyText.textContent;
        stickyText.textContent = "Added";
        setTimeout(() => {
          stickyText.textContent = originalStickyText;
        }, 2000);
      }

    }
    }
  }
});
}

document.addEventListener("DOMContentLoaded", function () {
  const stickyBar = document.querySelector(".sticky_product_bar");
  const target = document.querySelector(".variant__btn_cover");

  if (!stickyBar || !target) return;

  // always hide sticky bar on pageload
  stickyBar.style.display = "none";

  let hasScrolled = false; // track first scroll

  function toggleStickyBar() {
    const rect = target.getBoundingClientRect();
    const completelyOut = rect.bottom < 0 || rect.top > window.innerHeight;

    if (completelyOut) {
      stickyBar.style.display = "block"; // show sticky bar
    } else {
      stickyBar.style.display = "none"; // hide sticky bar
    }
  }

  // enable sticky check only after first scroll
  window.addEventListener("scroll", function () {
    if (!hasScrolled) {
      hasScrolled = true;
    }
    toggleStickyBar();
  });

  window.addEventListener("resize", function () {
    if (hasScrolled) toggleStickyBar();
  });

  ///////

  const stickyBuyBtn = document.querySelector(".sticky__btns .shopify-payment-button_sticky .shopify-payment-button__button");
  const customSelected = document.querySelector(".custom-dropdown__selected");

  if (!stickyBuyBtn) return;

  stickyBuyBtn.addEventListener("click", function (e) {
    e.preventDefault(); 
     if (this.classList.contains("default_sold")) {

          const notifyBtn = document.querySelector('.bitespeed-notify-me-button');
          if (notifyBtn) {
            $('.bitespeed-notify-me-button-parent button').click();
          }
        return;
      }

      if (this.classList.contains("default_available")) {
        const gokwikBtn = document.querySelector('[data-entity-id="gokwik-buy-now"]');
        if (gokwikBtn) gokwikBtn.click();
          return;
      }

    

    const selectedText = customSelected.childNodes[0].textContent.trim();
    let isDisabled = false;

    // find chip that matches selected size
    const selectedChip = document.querySelector(`.size__variant_picker input[value="${selectedText}"]`);
    if (selectedChip && selectedChip.classList.contains("disabled")) {
      isDisabled = true;
    }

    // Case 1: no size selected
    if (!selectedText || selectedText === "Size") {
      customSelected.classList.add("error");
      return;
    } else {
      customSelected.classList.remove("error");
    }

    // Case 2: selected size is disabled
    if (isDisabled) {
      stickyBuyBtn.textContent = "NOTIFY ME";
      stickyBuyBtn.classList.add("notify_mode");

      const notifyBtn = document.querySelector('.bitespeed-notify-me-button');
      if (notifyBtn) {
        $('.bitespeed-notify-me-button-parent button').click();
      }
      return;
    }

    // Case 3: selected size available
    stickyBuyBtn.textContent = "Buy Now"; // or restore original text if needed
    stickyBuyBtn.classList.remove("notify_mode");

    const gokwikBtn = document.querySelector('[data-entity-id="gokwik-buy-now"]');
    if (gokwikBtn) gokwikBtn.click();
  });
  
});


function updateStickyButtons() {
  const selectSizeBtnn = document.querySelector('.buttonSelectedSticky');
  const addToCartWrapp = document.querySelector('.sticky_addtocart_wrapper');
  const notifyBtnn = document.querySelector('.notify-custom-button-style');

  if (!selectSizeBtnn || !addToCartWrapp || !notifyBtnn) return;

  const selectedSize = document.querySelector(
    '.size__variant_picker input[type="radio"]:checked'
  );

  // No size selected
  if (!selectedSize) {
    selectSizeBtnn.style.display = 'block';
    addToCartWrapp.style.display = 'none';
    notifyBtnn.style.display = 'none';
    return;
  }

  // Size selected + Out of stock
  if (selectedSize.classList.contains('disabled')) {
    selectSizeBtnn.style.display = 'none';
    addToCartWrapp.style.display = 'none';
    notifyBtnn.style.display = 'block';
  }

  // Size selected + Available
  else {
    selectSizeBtnn.style.display = 'none';
    addToCartWrapp.style.display = 'flex';
    notifyBtnn.style.display = 'none';
  }
}


// Initial load
$(document).ready(function () {
  setTimeout(updateStickyButtons, 300);
});


// Size change
$(document).on(
  'change click',
  '.chip-wrapper input[type="radio"]',
  function () {
    setTimeout(updateStickyButtons, 300);
  }
);


// Notify button
$(document).on(
  'click',
  '.notify-custom-button-style',
  function (e) {
    e.preventDefault();

    $('.bitespeed-notify-me-button-parent button').trigger('click');
  }
);
// (() => {
//   const selectedBar = document.querySelector('.top-button-selected-bar');
//   const addCartBar = document.querySelector('.showing-add-cart-buy-btn');
//   const sizePicker = document.querySelector('.size__variant_picker');

//   if (!selectedBar || !addCartBar || !sizePicker) return;

//   const updateBars = () => {
//     const selected = sizePicker.querySelector('input[type="radio"]:checked');

//     selectedBar.style.display = selected ? 'none' : 'flex';
//     addCartBar.style.display = selected ? 'flex' : 'none';
//   };

//   updateBars();
//   sizePicker.addEventListener('change', updateBars);
// })();

// (() => {
//   const selectedBar = document.querySelector('.top-button-selected-bar');
//   const addCartBar = document.querySelector('.showing-add-cart-buy-btn');
//   const sizePicker = document.querySelector('.size__variant_picker');

//   if (!selectedBar || !addCartBar || !sizePicker) return;


//   sizePicker.addEventListener('change', () => {
//     selectedBar.style.display = 'none';
//     addCartBar.style.display = 'flex';
//   });
// })();


document.querySelector('.add-to-cart-design-cstm')?.addEventListener('click', function () {
  const button = this;
  const originalText = button.textContent;

  document.querySelector('button[name="add"]')?.click();

  button.textContent = 'Added';

  setTimeout(() => {
    button.textContent = originalText;
  }, 2300);
});

document.querySelector('.button-buy-design-cstm')?.addEventListener('click', () => {
  document.getElementById('gokwik-buy-now')?.click();
});

/* ----------------- End ---------------------------------*/